// THE READ MESSAGE, RENDERED IN PLACE inside its owning chat or Task timeline (BUILD-DEC-155).
// One renderer owns the envelope/body markup so email truth cannot drift across surfaces (AR-MOD-4).
//
// Unchanged posture: it renders the `comms` Message projection read-only. Recipients are resolved display
// refs, never raw addresses (G-058); the body is fetched behind `bodyRef` server-side, so the substrate
// pointer never crosses the wire. An unknown Message id renders an honest not-found, never a fabricated one
// (P-SELF). Tokens only (G-181).

import type { ProjectionState } from '../surfaces/useProjection.ts';
import type { MessageReaderProjection } from '../surfaces/know-projections.ts';

/** One of the three honest states (unavailable / loading / not-found) or the rendered Message. */
export function MessageReaderBody({
  state,
}: {
  readonly state: ProjectionState<MessageReaderProjection | undefined>;
}): React.JSX.Element {
  if (state.error) {
    return (
      <div className="em-body">
        <p>This message is temporarily unavailable.</p>
      </div>
    );
  }
  if (state.loading) {
    return (
      <div className="em-body" aria-busy="true">
        <p>Loading…</p>
      </div>
    );
  }
  if (state.data === null || state.data === undefined) {
    return (
      <div className="em-body">
        <p>This message could not be found.</p>
      </div>
    );
  }
  return <MessageBody message={state.data} />;
}

/** The rendered Message — envelope (from/to as resolved display refs), subject, body, and the footer. */
export function MessageBody({
  message,
}: {
  readonly message: MessageReaderProjection;
}): React.JSX.Element {
  return (
    <article data-message={message.id} data-direction={message.direction}>
      <div className="em-head">
        <h2 className="em-subj">{message.subject}</h2>
        <div className="em-meta">
          <Row label="From" value={message.fromDisplay} />
          {message.toDisplay.length > 0 ? (
            <Row label="To" value={message.toDisplay.join(', ')} />
          ) : null}
          <Row label="Date" value={message.sentAt ?? message.receivedAt ?? '—'} />
        </div>
      </div>
      <ReadMessageText message={message} />
      <div className="em-foot">
        <span className="hint">{messageRecordSummary(message)}</span>
      </div>
    </article>
  );
}

/** Direction + durable lifecycle + actor-authorised route context, with no inferred Task claim. */
export function messageRecordSummary(message: MessageReaderProjection): string {
  const status: Readonly<Record<MessageReaderProjection['status'], string>> = {
    draft: 'Draft',
    sent: 'Sent',
    captured: 'Captured without external delivery',
    received: 'Received',
    routed: 'Received and routed',
    discarded: 'Discarded',
  };
  const route = message.routeContext === 'task' ? 'this task' : 'this conversation';
  const direction = message.direction === 'inbound' ? 'Inbound email' : 'Outbound email';
  return `${direction} · ${status[message.status]} · On the record in ${route}`;
}

/**
 * The text of a read Message, or the truth about why there is none.
 *
 * A blank body region is the one thing this must never render. The owner opened a sent email in the chat log
 * and found an empty letter — which reads as "Ritona sent nobody anything", when what actually happened is
 * that the platform never kept a copy of what it sent. Those are completely different facts about the system,
 * and only one of them is true. So the absence is STATED, in the same place the words would have been.
 *
 * Three cases, three different sentences: the text is here; the text was never recorded (nothing to show, and
 * it is the platform's gap, not the message's); or the text was recorded and genuinely had nothing readable in
 * it. Guessing between the last two — as an empty string forces you to — is what produced the original lie.
 */
function ReadMessageText({
  message,
}: {
  readonly message: MessageReaderProjection;
}): React.JSX.Element {
  if (!message.bodyRecorded) {
    return (
      <div className="em-body" data-body-state="not-recorded">
        <p className="em-none">
          The text of this email was not recorded, so there is nothing to show here. The envelope
          above is what Ritona kept.
        </p>
      </div>
    );
  }
  if (message.body.trim() === '') {
    return (
      <div className="em-body" data-body-state="empty">
        <p className="em-none">This email carried no readable text.</p>
      </div>
    );
  }
  return (
    <>
      <EmailBodyText body={message.body} />
      <InboundReductionNotice reduction={message.inboundReduction} />
    </>
  );
}

/** State exactly which reduction Comms applied; never let the retained projection look unabridged. */
function InboundReductionNotice({
  reduction,
}: {
  readonly reduction: MessageReaderProjection['inboundReduction'];
}): React.JSX.Element | null {
  if (reduction === null || (!reduction.truncated && !reduction.quotedHistoryStripped)) return null;
  const notice = reduction.truncated
    ? reduction.quotedHistoryStripped
      ? 'Earlier quoted replies were removed, and this message was shortened to the retained-text limit.'
      : 'This message was shortened to the retained-text limit.'
    : "Earlier quoted replies were removed; this shows the sender's current message.";
  return (
    <div className="em-body" data-body-state="reduced">
      <p className="em-none">{notice}</p>
    </div>
  );
}

/**
 * A letter body: the server's text, split on blank lines into paragraphs. The ONLY transformation is
 * paragraphing — no markdown, no reflow, no trimming of the person's own words (G-160). Shared with the
 * inbound chat card, whose body is the sender's words carried on the trigger turn rather than a fetched
 * Message; both are server-authored text and both must read the same way.
 */
export function EmailBodyText({ body }: { readonly body: string }): React.JSX.Element {
  return (
    <div className="em-body">
      {body.split('\n\n').map((paragraph, index) => (
        <p key={index}>{paragraph}</p>
      ))}
    </div>
  );
}

/** One envelope row (prototype .em-row: mono key + value). */
function Row({
  label,
  value,
}: {
  readonly label: string;
  readonly value: string;
}): React.JSX.Element {
  return (
    <div className="em-row">
      <span className="em-k">{label}</span>
      <span className="em-v">{value}</span>
    </div>
  );
}
